// Mark citation UI explicitly so ordinary links, bracketed numbers, and hashes
// in the answer remain intact.
const EXCLUDED = '[data-clean-copy="exclude"], script, style, [hidden], [aria-hidden="true"]';
const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'DL',
  'DT',
  'DD',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TR',
  'UL',
]);

type TextFragment = {
  text: string;
  before?: number;
  after?: number;
  preformatted?: boolean;
};

const appendFragment = (
  text: string,
  previous: TextFragment,
  fragment: TextFragment,
  pendingSpace: boolean,
): string => {
  const gap = Math.max(previous.after ?? 0, fragment.before ?? 0);
  if (gap && text) {
    const before = previous.preformatted ? text : text.replace(/[\t ]+$/, '');
    const existingBreaks = /\n*$/.exec(before)?.[0].length ?? 0;
    const after = fragment.preformatted ? fragment.text : fragment.text.replace(/^ +/, '');
    return before + '\n'.repeat(Math.max(0, gap - existingBreaks)) + after;
  }
  if (previous.preformatted || fragment.preformatted) return text + fragment.text;
  const separator = pendingSpace && text && !text.endsWith(' ') ? ' ' : '';
  const before = text + separator;
  return before + (before.endsWith(' ') ? fragment.text.replace(/^ +/, '') : fragment.text);
};

// Adjacent block boundaries share a separator. Nesting layout containers must
// not introduce additional empty lines into the copied text.
const joinFragments = (fragments: TextFragment[]): TextFragment => {
  let text = '';
  let previous: TextFragment = { text: '' };
  let pendingSpace = false;
  let preformatted = false;
  for (const fragment of fragments) {
    if (!fragment.text) continue;
    if (fragment.text === ' ' && !fragment.preformatted) {
      pendingSpace = true;
      continue;
    }
    text = appendFragment(text, previous, fragment, pendingSpace);
    pendingSpace = false;
    previous = fragment;
    preformatted ||= !!fragment.preformatted;
  }
  if (pendingSpace && !previous.after) text += ' ';
  return { text, preformatted };
};

const alphabeticNumber = (value: number): string => {
  let text = '';
  for (let number = value; number > 0; number = Math.floor((number - 1) / 26)) {
    text = String.fromCharCode(97 + ((number - 1) % 26)) + text;
  }
  return text;
};

const romanNumber = (value: number): string => {
  const numerals: [number, string][] = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
  ];
  let remainder = value;
  let text = '';
  for (const [number, numeral] of numerals) {
    while (remainder >= number) {
      text += numeral;
      remainder -= number;
    }
  }
  return text;
};

const formatListNumber = (value: number, style: string): string => {
  if (value <= 0) return String(value);
  if (style === 'lower-alpha' || style === 'lower-latin') return alphabeticNumber(value);
  if (style === 'upper-alpha' || style === 'upper-latin')
    return alphabeticNumber(value).toUpperCase();
  if (value <= 3999 && style === 'lower-roman') return romanNumber(value);
  if (value <= 3999 && style === 'upper-roman') return romanNumber(value).toUpperCase();
  return style === 'decimal-leading-zero' ? String(value).padStart(2, '0') : String(value);
};

const HTML_LIST_STYLES: Record<string, string> = {
  a: 'lower-alpha',
  A: 'upper-alpha',
  i: 'lower-roman',
  I: 'upper-roman',
};
const BULLET_MARKERS: Record<string, string> = { circle: '◦', square: '▪' };

const getListStyle = (item: Element, list: Element): string => {
  const computedStyle =
    getComputedStyle(item).listStyleType || getComputedStyle(list).listStyleType;
  if (item instanceof HTMLElement && item.style.listStyleType) return item.style.listStyleType;
  if (list instanceof HTMLElement && list.style.listStyleType) return list.style.listStyleType;
  return HTML_LIST_STYLES[list.getAttribute('type') ?? ''] || computedStyle;
};

const integerAttribute = (element: Element, attribute: string, fallback: number): number => {
  const number = Number.parseInt(element.getAttribute(attribute) ?? '', 10);
  return Number.isSafeInteger(number) ? number : fallback;
};

const getListNumber = (item: Element, list: Element): number => {
  const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
  const step = list.hasAttribute('reversed') ? -1 : 1;
  let number = integerAttribute(list, 'start', step < 0 ? items.length : 1);
  for (const sibling of items) {
    number = integerAttribute(sibling, 'value', number);
    if (sibling === item) break;
    number += step;
  }
  return number;
};

const getListMarker = (item: Element): string => {
  const list = item.parentElement;
  if (!list?.matches('ol, ul')) return '';
  const style = getListStyle(item, list);
  if (style === 'none') return '';
  if (list.tagName === 'UL') return (BULLET_MARKERS[style] || '•') + ' ';
  return `${formatListNumber(getListNumber(item, list), style)}. `;
};

const listIndent = (item: Element): string => {
  let depth = -1;
  for (let parent = item.parentElement; parent; parent = parent.parentElement) {
    if (parent.matches('ol, ul')) depth += 1;
  }
  return '  '.repeat(Math.max(0, depth));
};

const whiteSpaceFor = (element: Element | null): string => {
  for (let current = element; current; current = current.parentElement) {
    const whiteSpace = getComputedStyle(current).whiteSpace;
    if (whiteSpace) return whiteSpace;
    if (current.tagName === 'PRE') return 'pre';
  }
  return 'normal';
};

const selectedText = (node: Node, range: Range, whiteSpace: string): TextFragment => {
  const start = node === range.startContainer ? range.startOffset : 0;
  const end = node === range.endContainer ? range.endOffset : node.textContent?.length;
  const text = (node.textContent ?? '').slice(start, end);
  if (['pre', 'pre-wrap', 'break-spaces'].includes(whiteSpace)) {
    return { text, preformatted: true };
  }
  return { text: text.replace(whiteSpace === 'pre-line' ? /[\t\r\f ]+/g : /[\t\r\n\f ]+/g, ' ') };
};

const blockGap = (node: Element, style: CSSStyleDeclaration): number => {
  const block = style.display
    ? !style.display.startsWith('inline') && style.display !== 'contents'
    : BLOCK_TAGS.has(node.tagName);
  if (!block) return 0;
  if (node.tagName !== 'P' && node.tagName !== 'PRE') return 1;
  return style.marginTop === '0px' && style.marginBottom === '0px' ? 1 : 2;
};

const plainText = (node: Node, range: Range, whiteSpace: string): TextFragment => {
  if (!range.intersectsNode(node)) return { text: '' };
  if (node.nodeType === Node.TEXT_NODE) return selectedText(node, range, whiteSpace);
  if (!(node instanceof Element)) return { text: '' };
  const style = getComputedStyle(node);
  if (node.matches(EXCLUDED) || style.display === 'none' || style.visibility === 'hidden') {
    return { text: '' };
  }
  if (node.tagName === 'BR') return { text: '\n' };
  const content = joinFragments(
    Array.from(node.childNodes).map((child) =>
      plainText(child, range, style.whiteSpace || whiteSpace),
    ),
  );
  if (!content.text) return content;
  if (node.tagName === 'LI') {
    const prefix = listIndent(node) + getListMarker(node);
    return {
      text: prefix + content.text.trim().replace(/\n(?![\t ])/g, '\n' + ' '.repeat(prefix.length)),
      before: 1,
      after: 1,
      preformatted: true,
    };
  }
  if (node.tagName === 'TD' || node.tagName === 'TH')
    return { ...content, text: content.text + '\t' };
  const gap = blockGap(node, style);
  return { ...content, before: gap, after: gap };
};

export const getCleanSelectionText = (selection: Selection | null): string => {
  if (!selection || selection.isCollapsed) return '';
  const parts: TextFragment[] = [];
  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);
    const ancestor = range.commonAncestorContainer;
    const element = ancestor instanceof Element ? ancestor : ancestor.parentElement;
    if (element?.closest(EXCLUDED)) continue;
    // Read the selected nodes in their original DOM context: cloning a range
    // loses list numbering, nesting, and inherited whitespace/display styles.
    const item = element?.closest('li');
    const startsInsideItem = item && range.startContainer === ancestor && range.startOffset > 0;
    const root = item && !startsInsideItem ? item : ancestor;
    parts.push(plainText(root, range, whiteSpaceFor(element)));
  }
  const text = parts
    .map((part) => part.text)
    .join('\n')
    .replace(/\u00a0/g, ' ');
  return parts.some((part) => part.preformatted)
    ? text.replace(/(?:^\n+)|(?:\n+$)/g, '')
    : text.trim();
};
